import React, { useState } from 'react';

interface TrackedKeyword {
  id: number;
  query: string;
  propertyUrl: string;
  initialPosition?: number;
  latestMetrics?: {
    position?: number;
    clicks?: number;
    impressions?: number;
    ctr?: number;
    trend?: 'up' | 'down' | 'stable' | 'new';
  };
  createdAt: string;
  updatedAt: string;
}

interface TrackedKeywordsPanelProps {
  trackedKeywords: TrackedKeyword[];
  isLoading?: boolean;
  onUntrackKeyword?: (keywordId: number) => Promise<void>;
  onRefreshMetrics?: () => Promise<void>;
}

// Gradient SVG Icons Component
const GradientIcon: React.FC<{ type: string; size?: string }> = ({ type, size = "w-5 h-5" }) => {
  const iconPaths: { [key: string]: string } = {
    track: "M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z",
    untrack: "M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0",
    refresh: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15",
    trending_up: "M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941",
    trending_down: "M2.25 6L9 12.75l4.286-4.286a11.948 11.948 0 014.306 6.43l.776 2.898m0 0l3.182-5.511m-3.182 5.511l-5.511-3.182",
    trending_stable: "M5 12h14"
  };

  return (
    <svg className={size} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id={`gradient-${type}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ec4899" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path
        d={iconPaths[type] || iconPaths.track}
        stroke={`url(#gradient-${type})`}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={type === 'track' ? `url(#gradient-${type})` : 'none'}
      />
    </svg>
  );
};

const TrackedKeywordsPanel: React.FC<TrackedKeywordsPanelProps> = ({
  trackedKeywords,
  isLoading = false,
  onUntrackKeyword,
  onRefreshMetrics
}) => {
  const [untrackingKeywords, setUntrackingKeywords] = useState<Set<number>>(new Set());
  const [refreshing, setRefreshing] = useState(false);

  const handleUntrackKeyword = async (keywordId: number) => {
    if (!onUntrackKeyword) return;
    
    setUntrackingKeywords(prev => new Set(prev).add(keywordId));
    
    try {
      await onUntrackKeyword(keywordId);
    } catch (error) {
      console.error('Error untracking keyword:', error);
    } finally {
      setUntrackingKeywords(prev => {
        const newSet = new Set(prev);
        newSet.delete(keywordId);
        return newSet;
      });
    }
  };

  const handleRefreshMetrics = async () => {
    if (!onRefreshMetrics) return;
    
    setRefreshing(true);
    try {
      await onRefreshMetrics();
    } catch (error) {
      console.error('Error refreshing metrics:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const formatPosition = (position?: number): string => {
    if (!position || position === 0) return '-';
    return position.toString();
  };

  const formatNumber = (num?: number): string => {
    if (!num || num === 0) return '-';
    return num.toLocaleString();
  };

  const formatCTR = (ctr?: number): string => {
    if (!ctr || ctr === 0) return '-';
    return `${(ctr * 100).toFixed(1)}%`;
  };

  const getTrendIcon = (trend?: string) => {
    switch (trend) {
      case 'up': return <GradientIcon type="trending_up" size="w-4 h-4" />;
      case 'down': return <GradientIcon type="trending_down" size="w-4 h-4" />;
      case 'stable': return <GradientIcon type="trending_stable" size="w-4 h-4" />;
      case 'new': return <span className="text-blue-600 text-sm">🌟</span>;
      default: return <span className="text-gray-400">-</span>;
    }
  };

  const getTrendColor = (trend?: string): string => {
    switch (trend) {
      case 'up': return 'text-green-600';
      case 'down': return 'text-red-600';
      case 'stable': return 'text-gray-600';
      case 'new': return 'text-blue-600';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <GradientIcon type="track" size="w-6 h-6" />
            Tracked Keywords
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Monitor your most important keywords across time
          </p>
        </div>
        {onRefreshMetrics && trackedKeywords.length > 0 && (
          <button
            onClick={handleRefreshMetrics}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            <GradientIcon type="refresh" size="w-4 h-4" />
            {refreshing ? 'Refreshing...' : 'Refresh Metrics'}
          </button>
        )}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-gray-500 bg-gray-100 transition ease-in-out duration-150">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Loading tracked keywords...
          </div>
        </div>
      )}

      {/* Keywords Table */}
      {!isLoading && trackedKeywords.length > 0 && (
        <div className="overflow-auto max-h-96 bg-white rounded-lg border border-gray-200 shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Keyword
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Initial Position
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Position
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CTR
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tracked Since
                </th>
                <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {trackedKeywords.map((keyword) => (
                <tr key={keyword.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 max-w-xs">
                    <div className="font-medium truncate" title={keyword.query}>
                      {keyword.query}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium text-blue-600">
                    {formatPosition(keyword.initialPosition)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium text-gray-900">
                    {formatPosition(keyword.latestMetrics?.position)}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className={getTrendColor(keyword.latestMetrics?.trend)} title={keyword.latestMetrics?.trend || 'No trend data'}>
                      {getTrendIcon(keyword.latestMetrics?.trend)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatNumber(keyword.latestMetrics?.clicks)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatNumber(keyword.latestMetrics?.impressions)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-600">
                    {formatCTR(keyword.latestMetrics?.ctr)}
                  </td>
                  <td className="px-3 py-3 text-center text-sm text-gray-500">
                    {new Date(keyword.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => handleUntrackKeyword(keyword.id)}
                      disabled={untrackingKeywords.has(keyword.id)}
                      className="text-xs bg-gradient-to-r from-red-500 to-red-600 text-white px-2 py-1 rounded hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                      title="Stop tracking this keyword"
                    >
                      {untrackingKeywords.has(keyword.id) ? '...' : 'Untrack'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && trackedKeywords.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <div className="flex justify-center">
            <GradientIcon type="track" size="w-12 h-12" />
          </div>
          <h3 className="mt-4 text-lg font-medium text-gray-900">No tracked keywords yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Start tracking keywords from the Historical Rankings table to monitor their performance over time.
          </p>
          <div className="mt-4 text-xs text-gray-400">
            Use the "Track" button next to any keyword in Historical Rankings to add it here.
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackedKeywordsPanel;